import Chip from '@mui/material/Chip';
import { Sync as SyncIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { cn } from 'src/utils/utility';

interface StatusChipProps {
  status: string;
  size?: 'small' | 'medium';
  className?: string;
}

const commonStyle = {
  borderRadius: '20px',
  fontWeight: 500,
};

const statusConfig: Record<string, { label: string; style: React.CSSProperties }> = {
  APPROVED: {
    label: 'Ready for Payment',
    style: {
      background: 'rgba(0, 153, 43, 0.12)',
      border: '1px solid #00992B',
      ...commonStyle,
      color: '#00992B',
    },
  },
  UPLOADED: {
    label: 'Uploaded',
    style: {
      background: 'rgba(0, 153, 43, 0.12)',
      border: '1px solid #00992B',
      ...commonStyle,
      color: '#00992B',
    },
  },
  TO_APPROVE: {
    label: 'To Approve',
    style: {
      background: 'rgba(38, 158, 204, 0.2)',
      border: '1px solid #269ECC',
      ...commonStyle,
      color: '#1D7699',
    },
  },
  TO_REJECT: {
    label: 'To Reject',
    style: {
      background: '#DFDFDF',
      border: '1px solid #475B69',
      ...commonStyle,
      color: '#475B69',
    },
  },
  REJECTED: {
    label: 'Rejected',
    style: {
      background: 'rgba(231, 37, 71, 0.20)',
      border: '1px solid #E72547',
      ...commonStyle,
      color: '#E72547',
    },
  },
  PENDING_DOCUMENTS: {
    label: 'Pending Document',
    style: {
      background: 'rgba(253, 202, 64, 0.20)',
      border: '1px solid #FDCA40',
      ...commonStyle,
      color: '#E3B539',
    },
  },
  PENDING_DOCUMENT: {
    label: 'Pending Document',
    style: {
      background: 'rgba(253, 202, 64, 0.20)',
      border: '1px solid #FDCA40',
      ...commonStyle,
      color: '#E3B539',
    },
  },
  FLAGGED: {
    label: 'Flagged',
    style: {
      background: 'rgba(247, 152, 36, 0.2)',
      border: '1px solid #F79824',
      ...commonStyle,
      color: '#E58D21',
    },
  },
  NO_CONTRACT_FOUND: {
    label: 'Flagged',
    style: {
      background: 'rgba(247, 152, 36, 0.2)',
      border: '1px solid #F79824',
      ...commonStyle,
      color: '#E58D21',
    },
  },
  NOT_AN_INVOICE: {
    label: 'Invalid File',
    style: {
      background: 'rgba(105, 123, 212, 0.12)',
      border: '1px solid #697BD4',
      ...commonStyle,
      color: '#697BD4',
    },
  },
  MISSING_REQUIRED_FIELD: {
    label: 'Missing Required Fields',
    style: {
      background: 'rgb(255, 255, 255)',
      border: '1px solid #E72547',
      ...commonStyle,
      color: '#E72547',
    },
  },
  RESOLVED_AS_NOT_AN_INVOICE: {
    label: 'Invalid File',
    style: {
      background: 'rgba(105, 123, 212, 0.12)',
      border: '1px solid #697BD4',
      ...commonStyle,
      color: '#697BD4',
    },
  },
  RESOLVED_AS_INCONSISTENT_SHIPMENT_DATA: {
    label: 'Inconsistent Shipment Data',
    style: {
      background: 'rgb(255, 255, 255)',
      border: '1px solid #F79824',
      ...commonStyle,
      color: '#F79824',
    },
  },
  INCONSISTENT_SHIPMENT_DATA: {
    label: 'Inconsistent Shipment Data',
    style: {
      background: 'rgb(255, 255, 255)',
      border: '1px solid #F79824',
      ...commonStyle,
      color: '#F79824',
    },
  },
  RESOLVED_AS_MISSING_REQUIRED_FIELD: {
    label: 'Missing Required Fields',
    style: {
      background: 'rgb(255, 255, 255)',
      border: '1px solid #E72547',
      ...commonStyle,
      color: '#E72547',
    },
  },
  DUPLICATE: {
    label: 'Duplicate',
    style: {
      background: 'rgb(255, 255, 255)',
      border: '1px solid #E72547',
      ...commonStyle,
      color: '#E72547',
    },
  },
  PARTIAL: {
    label: 'Partial',
    style: {
      background: 'rgba(247, 152, 36, 0.2)',
      border: '1px solid #F79824',
      ...commonStyle,
      color: '#E58D21',
    },
  },
  PIPELINE_TRIGGER_FAILED: {
    label: 'Pipeline Trigger Failed',
    style: {
      background: 'rgba(231, 37, 71, 0.20)',
      border: '1px solid #E72547',
      ...commonStyle,
      color: '#E72547',
    },
  },
  UNDER_REVIEW: {
    label: 'Under Review',
    style: {
      background: 'rgba(89, 144, 130, 0.12)',
      border: '1px solid #597282',
      ...commonStyle,
      color: '#597282',
    },
  },
  ON_PROCESSING: {
    label: 'Processing',
    style: {
      background: '#ffffff',
      border: '1px solid #D9D9D9',
      ...commonStyle,
      color: '#A3A3A3',
    },
  },
};

const ResponsiveChip = styled(Chip)(({ theme }) => ({
  // Small size styling
  '&.MuiChip-sizeSmall': {
    [theme.breakpoints.down(1520)]: {
      height: '20px',
      fontSize: '10px',
    },
  },
  // Medium size styling
  '&.MuiChip-sizeMedium': {
    [theme.breakpoints.down(1520)]: {
      fontSize: '12px!important',
      height: '28px',
    },
    '& .MuiChip-label': {
      [theme.breakpoints.down(1520)]: {
        fontSize: '12px!important',
      },
    },
  },
}));

const StatusChip: React.FC<StatusChipProps> = ({ status, size = 'medium', className }) => {
  if (!status) {
    status = 'ON_PROCESSING'; // Default to ON_PROCESSING if status is not provided
  }
  const config = statusConfig[status] || statusConfig['UNDER_REVIEW']; // Fallback to UNDER_REVIEW if status is not found
  return (
    <div className={cn('inline-block', className)}>
      <ResponsiveChip
        label={
          status === 'ON_PROCESSING' ? (
            <div className='flex items-center gap-10'>
              <span>Processing</span>
              <SyncIcon className='text-[#A3A3A3] animate-spin-reverse' sx={{ fontSize: '16px' }} />
            </div>
          ) : (
            config.label
          )
        }
        style={config.style}
        size={size}
      />
    </div>
  );
};

export default StatusChip;
